import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { toast } from "sonner";
import { Building2, Loader2, Lock } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Input } from "@/components/ui/Input";
import { Label } from "@/components/ui/Label";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/Card";
import { useOnboardingStore } from "@/stores/onboardingStore";
import { useAuth } from "@/contexts/AuthContext";
import { onboardingApi, ApiError } from "@/lib/api";

// Basic phone regex — accepts leading +, digits, spaces, hyphens, parens.
// Deliberately permissive; the backend is the source of truth.
const PHONE_RE = /^[+]?[\d\s\-()]{7,}$/;
// 2-letter ISO 3166-1 alpha-2 country code (uppercased before submit).
const COUNTRY_RE = /^[A-Za-z]{2}$/;

const schema = z.object({
  first_name: z.string().min(1, "Required"),
  last_name: z.string().min(1, "Required"),
  business_name: z.string().min(2, "At least 2 characters"),
  // Structured address — line2 is the only optional address field.
  address_line1: z.string().min(1, "Required"),
  address_line2: z.string().optional(),
  city: z.string().min(1, "Required"),
  region: z.string().min(1, "Required"),
  postal_code: z.string().min(1, "Required"),
  country_code: z
    .string()
    .regex(COUNTRY_RE, "2-letter country code (e.g. US, GB, IN)"),
  business_website: z
    .string()
    .url("Must be a valid URL (start with https://)"),
  business_phone: z
    .string()
    .regex(PHONE_RE, "Enter a valid phone number"),
  owner_phone: z
    .string()
    .regex(PHONE_RE, "Enter a valid phone number"),
});
type FormValues = z.infer<typeof schema>;

export function StepCompanyForm() {
  const { idToken } = useAuth();
  const onboardingId = useOnboardingStore((s) => s.onboardingId);
  const email = useOnboardingStore((s) => s.email);
  const setCompany = useOnboardingStore((s) => s.setCompany);
  const setStep = useOnboardingStore((s) => s.setStep);

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    mode: "onTouched",
    defaultValues: {
      first_name: "",
      last_name: "",
      business_name: "",
      address_line1: "",
      address_line2: "",
      city: "",
      region: "",
      postal_code: "",
      country_code: "",
      business_website: "",
      business_phone: "",
      owner_phone: "",
    },
  });

  const onSubmit = async (values: FormValues) => {
    if (!onboardingId) {
      toast.error("Please restart onboarding");
      return;
    }
    if (!idToken) {
      toast.error("Please sign in again");
      return;
    }
    try {
      const res = await onboardingApi.submitCompany(onboardingId, values, idToken);
      setCompany(res.client_id, res.client_code);
      setStep("subdomain");
      toast.success("Saved");
    } catch (err) {
      const ae = err as ApiError;
      if (ae.status === 0 || ae.code === "not_implemented") {
        // Dev fallback — synthesize a placeholder client_code so the rest of
        // the wizard can progress.
        const placeholder = values.business_name
          .toUpperCase()
          .replace(/[^A-Z0-9]/g, "")
          .slice(0, 16) || "NEWCO";
        setCompany("", placeholder);
        setStep("subdomain");
        toast.message("Backend unavailable — continuing locally");
        return;
      }
      console.error("submitCompany", ae);
      toast.error("Couldn't save company info", { description: ae.message });
    }
  };

  const errors = form.formState.errors;
  // Submit disabled until form valid.
  const allValid = form.formState.isValid;

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center gap-3">
          <div className="rounded-md bg-brand-violet/15 p-2 text-brand-violet">
            <Building2 className="h-5 w-5" />
          </div>
          <div>
            <CardTitle>Tell us about your business</CardTitle>
            <CardDescription>
              A few quick details so we can set up your workspace.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        <p className="mb-4 text-xs text-muted-foreground">
          Fields marked with <span className="text-signal-failed">*</span> are required.
        </p>
        <form className="space-y-4" onSubmit={form.handleSubmit(onSubmit)} noValidate>
          {/* Owner email — prefilled and readonly. */}
          <div className="space-y-1.5">
            <Label htmlFor="owner-email">Owner email</Label>
            <div className="relative">
              <Input
                id="owner-email"
                type="email"
                value={email ?? ""}
                readOnly
                tabIndex={-1}
                aria-readonly
                className="bg-muted/30 text-muted-foreground pr-9 cursor-not-allowed"
              />
              <Lock className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              id="first_name"
              label="Owner first name"
              error={errors.first_name?.message}
            >
              <Input
                id="first_name"
                autoComplete="given-name"
                {...form.register("first_name")}
              />
            </FormField>
            <FormField
              id="last_name"
              label="Owner last name"
              error={errors.last_name?.message}
            >
              <Input
                id="last_name"
                autoComplete="family-name"
                {...form.register("last_name")}
              />
            </FormField>
          </div>

          <FormField
            id="business_name"
            label="Business name"
            error={errors.business_name?.message}
          >
            <Input
              id="business_name"
              autoComplete="organization"
              {...form.register("business_name")}
            />
          </FormField>

          <FormField
            id="address_line1"
            label="Address line 1"
            error={errors.address_line1?.message}
          >
            <Input
              id="address_line1"
              autoComplete="address-line1"
              {...form.register("address_line1")}
            />
          </FormField>

          <FormField
            id="address_line2"
            label="Address line 2"
            required={false}
            error={errors.address_line2?.message}
          >
            <Input
              id="address_line2"
              autoComplete="address-line2"
              placeholder="Apt, suite, floor (optional)"
              {...form.register("address_line2")}
            />
          </FormField>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField id="city" label="City" error={errors.city?.message}>
              <Input
                id="city"
                autoComplete="address-level2"
                {...form.register("city")}
              />
            </FormField>
            <FormField
              id="region"
              label="State / Region"
              error={errors.region?.message}
            >
              <Input
                id="region"
                autoComplete="address-level1"
                placeholder="e.g. CA"
                {...form.register("region")}
              />
            </FormField>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              id="postal_code"
              label="Zip / Postal code"
              error={errors.postal_code?.message}
            >
              <Input
                id="postal_code"
                autoComplete="postal-code"
                {...form.register("postal_code")}
              />
            </FormField>
            <FormField
              id="country_code"
              label="Country"
              error={errors.country_code?.message}
            >
              <Input
                id="country_code"
                autoComplete="country"
                placeholder="2-letter ISO code, e.g. US"
                maxLength={2}
                {...form.register("country_code")}
              />
            </FormField>
          </div>

          <FormField
            id="business_website"
            label="Business website"
            error={errors.business_website?.message}
          >
            <Input
              id="business_website"
              type="url"
              placeholder="https://"
              autoComplete="url"
              {...form.register("business_website")}
            />
          </FormField>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FormField
              id="business_phone"
              label="Business phone"
              error={errors.business_phone?.message}
            >
              <Input
                id="business_phone"
                type="tel"
                autoComplete="tel"
                {...form.register("business_phone")}
              />
            </FormField>
            <FormField
              id="owner_phone"
              label="Owner phone"
              error={errors.owner_phone?.message}
            >
              <Input
                id="owner_phone"
                type="tel"
                autoComplete="tel"
                {...form.register("owner_phone")}
              />
            </FormField>
          </div>

          <Button
            type="submit"
            size="lg"
            className="w-full"
            disabled={!allValid || form.formState.isSubmitting}
          >
            {form.formState.isSubmitting ? <Loader2 className="h-4 w-4 animate-spin" /> : null}
            Continue
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}

function FormField({
  id,
  label,
  error,
  required = true,
  children,
}: {
  id: string;
  label: string;
  error?: string;
  required?: boolean;
  children: React.ReactNode;
}) {
  const describedBy = error ? `${id}-error` : undefined;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>
        {label}
        {required ? (
          <span aria-hidden="true" className="ml-1 text-signal-failed">
            *
          </span>
        ) : null}
      </Label>
      {children}
      {error && (
        <p id={describedBy} className="text-xs text-signal-failed">
          {error}
        </p>
      )}
    </div>
  );
}
